import { useCallback, useDeferredValue, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { PeriodPreset } from '@afrilink/ui';
import {
  DEFAULT_PERIOD,
  inPeriod,
  stepPeriod,
  useFilterParams,
  type FilterParams,
  type PeriodRange,
} from './useFilterParams';

/**
 * useTableFilters — period + search + facet state for a LIST TABLE.
 *
 * Sibling of `useFilterParams`, which serves dashboards. The split exists
 * because the two surfaces have opposite defaults and opposite risks:
 *
 *  - A dashboard asks "how are we doing *this month*" → default `this-month`.
 *  - A table asks "show me the records" → default `all-time` (Rule T-1). A
 *    customer list defaulting to this month renders 1 of 22 rows and reads as
 *    data loss, not as a filter.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * AR-5 — THE RULE THIS HOOK EXISTS TO ENFORCE
 *
 * A client-side filter may only be applied to a row set the client has
 * COMPLETELY. Every list query in this portal is `ORDER BY <date> DESC LIMIT N`
 * — a truncating window, not a complete set. Compose a client-side date filter
 * with one of those and you get a control that structurally cannot reach older
 * rows and FAILS SILENTLY: pick "last year" on a page holding the 500 most
 * recent invoices and the table renders empty, which reads as "we issued none".
 *
 * So the date filter has two modes and the caller must choose deliberately:
 *
 *  - `dateMode: 'server'` — the resolved range goes to the service as
 *    `gte`/`lte`. Correct for every FLOW table. The period joins the query key
 *    (AR-1.2's own stated exception, now met).
 *  - `dateMode: 'client'` — filtered here. Lawful ONLY where the fetch is
 *    provably complete, which in practice means a small stock table, and only
 *    with the AR-6 truncation sentinel wired so the day it stops being true is
 *    visible.
 *
 * Search stays client-side in both modes (Rule T-3): a keystroke must never
 * produce a network request. That is honest only while the caption discloses
 * the window (AR-6) — searching 200 of 4,000 rows without saying so is worse
 * than not offering search.
 * ───────────────────────────────────────────────────────────────────────────
 */

export type DateMode = 'client' | 'server' | 'none';

export interface TableFilterOptions<T> {
  /** The rows currently fetched. `undefined` while loading. */
  rows: T[] | undefined;

  /**
   * How the period filter is applied. Defaults to `'none'` — a table with no
   * date question must not grow one by accident.
   */
  dateMode?: DateMode;

  /**
   * Event timestamp accessor. REQUIRED when `dateMode === 'client'`, unused
   * otherwise (in server mode the database does the comparison).
   *
   * Rule S-1: only ever an EVENT timestamp (`paid_at`, `issue_date`,
   * `created_at`). Pointing this at a state column silently redefines what the
   * table counts.
   */
  dateOf?: (row: T) => string | null | undefined;

  /**
   * What the date filter actually filters, in the user's words — "Created",
   * "Paid", "Expires", "Opened".
   *
   * REQUIRED whenever `dateMode !== 'none'`, and required by the TYPE, not by
   * convention (Rule T-2, made structural by amendment A6). A bare "Period" on
   * a customer list silently reframes "customers" as "customers created in
   * period" — a wrong number that looks right. The label is the whole defence,
   * so it cannot be the optional prop everyone forgets.
   */
  dateLabel?: string;

  /** Haystack for one row. Lowercased once per row per pass, not per comparison. */
  searchText?: (row: T) => string;

  /** Facet predicates (state, kind, department). ANDed after period + search. */
  facets?: Array<(row: T) => boolean>;

  /** Rule T-1: tables default to all-time. Override only with a reason. */
  defaultPeriod?: PeriodPreset;

  /**
   * AR-6 — did the service truncate? Pass the `truncated` flag the service
   * returns from its `limit + 1` probe.
   *
   * Omitting it asserts the fetch is complete. That assertion is exactly what
   * went unchecked on `AuditLogPage` for the whole of its life, so prefer
   * passing `false` explicitly over leaving it undefined.
   */
  truncated?: boolean;

  /** The cap the service applied, for the truncated caption. */
  limit?: number;
}

export interface TableFilterResult<T> {
  /** Rows after period (client mode only), search and facets. */
  rows: T[];
  /** Rows fetched, before any client-side filtering. */
  totalCount: number;
  /** Rows after filtering. */
  resultCount: number;
  /** The input is ahead of the deferred term — drives `SearchField.pending`. */
  isSearching: boolean;
  /** Raw search term (drives the input). */
  q: string;
  setQ: (next: string) => void;
  /** Resolved SAST bounds. Hand these to the service in `'server'` mode. */
  range: PeriodRange;
  /** Full period/company/sku surface. */
  filters: FilterParams;
  /** Step a closed period. No-op on an open one. */
  stepPeriodBy: (direction: -1 | 1) => void;
  /** Rule T-4 + AR-6 caption. Render it on `UniversalTable`. */
  caption: string;
  /** True when the caption is reporting a truncated window. */
  isTruncated: boolean;
}

/** Rule T-1: a table shows everything unless asked otherwise. */
export const TABLE_DEFAULT_PERIOD: PeriodPreset = 'all-time';

export function useTableFilters<T>(opts: TableFilterOptions<T>): TableFilterResult<T> {
  const {
    rows,
    dateMode = 'none',
    dateOf,
    dateLabel,
    searchText,
    facets,
    defaultPeriod = TABLE_DEFAULT_PERIOD,
    truncated = false,
    limit,
  } = opts;

  // Fail loudly in development rather than shipping an unlabelled date control.
  // A thrown error in dev is cheaper than the silent reframing T-2 prevents.
  if (import.meta.env.DEV && dateMode !== 'none' && !dateLabel) {
    throw new Error(
      'useTableFilters: `dateLabel` is required when `dateMode` is set (Rule T-2). ' +
        'Name the column being filtered — "Created", "Paid", "Expires" — never "Period".',
    );
  }
  if (import.meta.env.DEV && dateMode === 'client' && !dateOf) {
    throw new Error(
      'useTableFilters: `dateOf` is required when `dateMode` is "client".',
    );
  }

  const filters = useFilterParams({ defaultPeriod });
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';

  const setQ = useCallback(
    (next: string) =>
      setParams(
        (prev) => {
          const url = new URLSearchParams(prev);
          if (next) url.set('q', next);
          else url.delete('q');
          return url;
        },
        // `replace` so typing does not push one history entry per character —
        // the back button must leave the page, not walk the search term back.
        { replace: true },
      ),
    [setParams],
  );

  // AR-1.2 holds for search in both modes: the deferred term is a VIEW concern.
  // It never joins a query key, so typing cannot trigger a refetch (Rule T-3).
  const deferredQ = useDeferredValue(q);
  const isSearching = q !== deferredQ;

  const { range } = filters;

  const filtered = useMemo(() => {
    if (!rows) return [];
    const term = deferredQ.trim().toLowerCase();

    // One pass, one predicate chain. Chaining `.filter()` calls would walk the
    // rows three times for the same result.
    return rows.filter((row) => {
      // In 'server' mode the database has already applied the range; re-applying
      // it here would be a second, weaker filter over a truncated set.
      if (dateMode === 'client' && dateOf && !inPeriod(dateOf(row), range)) return false;

      if (facets) {
        for (const pass of facets) if (!pass(row)) return false;
      }

      if (term && searchText) {
        if (!searchText(row).toLowerCase().includes(term)) return false;
      }

      return true;
    });
  }, [rows, dateMode, dateOf, facets, searchText, deferredQ, range]);

  const totalCount = rows?.length ?? 0;
  const resultCount = filtered.length;

  const stepPeriodBy = useCallback(
    (direction: -1 | 1) => {
      const next = stepPeriod(filters.period, range, direction);
      if (!next) return;
      filters.setPeriod({ preset: filters.period, from: next.from, to: next.to });
    },
    [filters, range],
  );

  /**
   * Rule T-4 + AR-6.
   *
   * Three distinct statements, because they are three different facts and
   * collapsing them is how "no results" and "no data" became indistinguishable:
   *   complete + unfiltered → "44 invoices"
   *   complete + filtered   → "12 of 44"
   *   truncated             → "…more rows exist. Narrow the period."
   */
  const caption = useMemo(() => {
    const noun = resultCount === 1 ? 'row' : 'rows';
    if (truncated) {
      const cap = limit ?? totalCount;
      return resultCount === totalCount
        ? `${cap} ${noun} shown — more rows exist. Narrow the ${(dateLabel ?? 'date').toLowerCase()} range.`
        : `${resultCount} of ${cap} shown — more rows exist. Narrow the ${(dateLabel ?? 'date').toLowerCase()} range.`;
    }
    if (resultCount === totalCount) return `${totalCount} ${noun}`;
    return `${resultCount} of ${totalCount}`;
  }, [resultCount, totalCount, truncated, limit, dateLabel]);

  return {
    rows: filtered,
    totalCount,
    resultCount,
    isSearching,
    q,
    setQ,
    range,
    filters,
    stepPeriodBy,
    caption,
    isTruncated: truncated,
  };
}

export { DEFAULT_PERIOD };
